import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Tabs, SlideOver, SearchInput, Badge, Button, Card, EmptyState } from '@teals/ui-web';
import {
  useGetIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useTestIntegrationMutation,
  type Integration,
} from '../../store/api';
import { useAuth } from '../../hooks';

const CATEGORIES = ['All', 'LLM Providers', 'Storage', 'Notifications', 'Observability'] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_TYPE_MAP: Record<string, Category> = {
  llm: 'LLM Providers',
  storage: 'Storage',
  notification: 'Notifications',
  observability: 'Observability',
};

function resolveCategory(integrationType: string): Category {
  const key = integrationType.toLowerCase();
  for (const [fragment, category] of Object.entries(CATEGORY_TYPE_MAP)) {
    if (key.includes(fragment)) return category;
  }
  return 'All';
}

const STATUS_VARIANT: Record<string, 'success' | 'neutral' | 'error'> = {
  connected: 'success',
  disconnected: 'neutral',
  error: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  connected: 'Connected',
  disconnected: 'Available',
  error: 'Error',
};

const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const PageHeaderLeft = styled.div`
  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.25rem;
    color: var(--text-primary);
  }
  p {
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const IntegrationCard = styled(Card)`
  cursor: pointer;
  transition: all 0.25s;
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;

  &:hover {
    border-color: var(--accent);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
  margin-left: 0.75rem;
`;

const IntegrationName = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IntegrationType = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: var(--text-muted);
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
`;

const CardFooterStats = styled.div`
  display: flex;
  gap: 1rem;
`;

const ConfigureLink = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--accent);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const PanelSection = styled.section`
  margin-bottom: 1.5rem;
`;

const PanelSectionTitle = styled.h4`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  color: var(--text-muted);
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

const ConfigField = styled.div`
  margin-bottom: 0.75rem;
`;

const ConfigLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const ConfigInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }
`;

const PanelActions = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
`;

const TestResult = styled.div<{ $success: boolean }>`
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  background: ${({ $success }) => ($success ? 'var(--success-subtle)' : 'var(--error-subtle)')};
  color: ${({ $success }) => ($success ? 'var(--success)' : 'var(--error)')};
`;

const Integrations: React.FC = () => {
  const { user: _user } = useAuth();
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [search, setSearch] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [configDraft, setConfigDraft] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<{ status: string; message: string } | null>(null);

  const { data, isLoading, isError } = useGetIntegrationsQuery();
  const [_createIntegration] = useCreateIntegrationMutation();
  const [updateIntegration, { isLoading: isUpdating }] = useUpdateIntegrationMutation();
  const [deleteIntegration, { isLoading: isDeleting }] = useDeleteIntegrationMutation();
  const [testIntegration, { isLoading: isTesting }] = useTestIntegrationMutation();

  const integrations = data?.integrations ?? [];

  const filtered = useMemo(() => {
    return integrations.filter((item) => {
      if (activeCategory !== 'All' && resolveCategory(item.integrationType) !== activeCategory) {
        return false;
      }
      if (search && !item.name.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [integrations, activeCategory, search]);

  const openPanel = (integration: Integration) => {
    setSelectedIntegration(integration);
    setTestResult(null);
    const draft: Record<string, string> = {};
    if (integration.config) {
      Object.entries(integration.config).forEach(([key, val]) => {
        draft[key] = typeof val === 'string' ? val : JSON.stringify(val);
      });
    }
    setConfigDraft(draft);
  };

  const closePanel = () => {
    setSelectedIntegration(null);
    setTestResult(null);
  };

  const handleConfigChange = (key: string, value: string) => {
    setConfigDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!selectedIntegration) return;
    await updateIntegration({
      id: selectedIntegration.integrationId,
      body: { config: configDraft },
    });
    closePanel();
  };

  const handleDelete = async () => {
    if (!selectedIntegration) return;
    await deleteIntegration(selectedIntegration.integrationId);
    closePanel();
  };

  const handleTest = async () => {
    if (!selectedIntegration) return;
    try {
      const result = await testIntegration(selectedIntegration.integrationId).unwrap();
      setTestResult({ status: result.status, message: result.message });
    } catch {
      setTestResult({ status: 'error', message: 'Connection test failed' });
    }
  };

  const tabs = CATEGORIES.map((cat) => ({
    id: cat,
    label: cat,
  }));

  return (
    <PageBody className="fade-in">
      <Container>
        <PageHeader>
          <PageHeaderLeft>
            <h1>Integrations</h1>
            <p>Connect external services to your AI agents</p>
          </PageHeaderLeft>
          <Button variant="primary">Add Integration</Button>
        </PageHeader>

        <Tabs
          tabs={tabs}
          activeTab={activeCategory}
          onChange={(id: string) => setActiveCategory(id as Category)}
        />

        <Toolbar>
          <SearchInput
            placeholder="Search integrations..."
            value={search}
            onChange={setSearch}
            style={{ minWidth: 280 }}
          />
        </Toolbar>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--text-muted)', fontSize: '0.9375rem' }}>
            Loading integrations...
          </div>
        )}

        {isError && (
          <div style={{ textAlign: 'center', padding: '4rem 0', color: 'var(--error)', fontSize: '0.9375rem' }}>
            Failed to load integrations.
          </div>
        )}

        {!isLoading && !isError && filtered.length === 0 && (
          <EmptyState
            title="No integrations found"
            description={
              activeCategory === 'All'
                ? 'Add your first integration to get started.'
                : `No ${activeCategory.toLowerCase()} integrations available.`
            }
          />
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <Grid>
            {filtered.map((item) => (
              <IntegrationCard
                key={item.integrationId}
                elevation="sm"
                padding="1.5rem"
                onClick={() => openPanel(item)}
              >
                <CardContent>
                  <CardTopRow>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IconBox>{item.name[0]?.toUpperCase()}</IconBox>
                      <CardInfo>
                        <IntegrationName>{item.name}</IntegrationName>
                        <IntegrationType>{item.integrationType}</IntegrationType>
                      </CardInfo>
                    </div>
                    <Badge variant={STATUS_VARIANT[item.status] ?? 'neutral'} size="sm">
                      {STATUS_LABEL[item.status] ?? item.status}
                    </Badge>
                  </CardTopRow>
                  <CardFooter>
                    <CardFooterStats>
                      <span>{item.connectedAgents?.length ?? 0} agents</span>
                      <span>Added {new Date(item.createdAt).toLocaleDateString()}</span>
                    </CardFooterStats>
                    <ConfigureLink>
                      {item.status === 'connected' ? 'Configure' : 'Connect'}
                    </ConfigureLink>
                  </CardFooter>
                </CardContent>
              </IntegrationCard>
            ))}
          </Grid>
        )}

        <SlideOver
          open={!!selectedIntegration}
          onClose={closePanel}
          title={selectedIntegration?.name ?? 'Integration'}
        >
          {selectedIntegration && (
            <>
              <PanelSection>
                <PanelSectionTitle>Details</PanelSectionTitle>
                <DetailRow>
                  <DetailLabel>Name</DetailLabel>
                  <DetailValue>{selectedIntegration.name}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>{selectedIntegration.integrationType}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <Badge variant={STATUS_VARIANT[selectedIntegration.status] ?? 'neutral'} size="sm">
                    {STATUS_LABEL[selectedIntegration.status] ?? selectedIntegration.status}
                  </Badge>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Connected Agents</DetailLabel>
                  <DetailValue>{selectedIntegration.connectedAgents?.length ?? 0}</DetailValue>
                </DetailRow>
              </PanelSection>

              <PanelSection>
                <PanelSectionTitle>Configuration</PanelSectionTitle>
                {Object.keys(configDraft).length === 0 ? (
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                    No configuration fields.
                  </p>
                ) : (
                  Object.entries(configDraft).map(([key, value]) => (
                    <ConfigField key={key}>
                      <ConfigLabel>{key}</ConfigLabel>
                      <ConfigInput
                        value={value}
                        onChange={(e) => handleConfigChange(key, e.target.value)}
                      />
                    </ConfigField>
                  ))
                )}
              </PanelSection>

              <Button
                variant="secondary"
                onClick={handleTest}
                disabled={isTesting}
                style={{ width: '100%', marginBottom: '0.5rem' }}
              >
                {isTesting ? 'Testing...' : 'Test Connection'}
              </Button>

              {testResult && (
                <TestResult $success={testResult.status !== 'error'}>
                  {testResult.message}
                </TestResult>
              )}

              <PanelActions>
                <Button variant="primary" onClick={handleSave} disabled={isUpdating} style={{ flex: 1 }}>
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </Button>
              </PanelActions>
            </>
          )}
        </SlideOver>
      </Container>
    </PageBody>
  );
};

export default Integrations;
